import {useContext, useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import {ConnectContactResponse} from '@/api/dto/connect';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useRosterStore} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import ExpandMoreButton from '@common/timeline/ExpandMoreButton';
import {
  PlayerRow,
  CompactButtonStyles,
  compactPlayButtonClass,
  CompactAudio,
} from '@/components/pages/tasks/aws_communications/contact/AwsCommunicationsContactVoicemail.styles';

type Props = {
  connectContact: ConnectContactResponse;
  rosterId?: number;
}

const RECORDING_UNAVAILABLE = 'This voicemail recording is not available.';

export default function AwsCommunicationsContactVoicemail({ connectContact, rosterId }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { currentRoster } = useRosterStore();
  const { addAlert } = useAlertStore();

  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Prefer the contact's own roster, then the roster resolved by the drawer (the "assumed" roster on
  // the cross-roster Tasks page where currentRoster is null -- see CARECO-5939), then the store.
  const effectiveRosterId = connectContact.rosterId ?? rosterId ?? currentRoster?.id;

  const { mutate: loadRecording, isPending } = useMutation({
    mutationFn: async () =>
      carecoApi?.getConnectContactPresignedUrl(connectContact.id, effectiveRosterId!),
    onSuccess(url) {
      if (url) {
        setAudioUrl(url);
      } else {
        addAlert({ type: ToastType.ERROR, content: RECORDING_UNAVAILABLE });
      }
    },
    onError(_error: AxiosError) {
      addAlert({ type: ToastType.ERROR, content: RECORDING_UNAVAILABLE });
    },
  });

  if (!connectContact.voicemailTranscript?.length) {
    return null;
  }

  return (
    <div>
      {connectContact.voicemailTranscript.slice(0, 120)}
      {connectContact.voicemailTranscript.length > 120 && (
        <ExpandMoreButton
          buttonText={(isOpen) => isOpen ? 'show less' : 'show more'}
          expandItems={[connectContact.voicemailTranscript.slice(120)]}
          useExpandHideText={false}
          active={false}
          inline
        />
      )}

      <PlayerRow>
        {audioUrl ? (
          <CompactAudio
            controls
            autoPlay
            src={audioUrl}
            data-testid="voicemail-audio"
            onError={() => {
              setAudioUrl(null);
              addAlert({ type: ToastType.ERROR, content: RECORDING_UNAVAILABLE });
            }}
          />
        ) : (
          <CompactButtonStyles>
            <Button
              styleType={StyleType.SECONDARY}
              disabled={isPending || !effectiveRosterId}
              onClick={() => loadRecording()}
              aria-label="Play voicemail"
              testId="voicemail-play"
              className={compactPlayButtonClass}
            >
              {isPending ? 'Loading…' : 'Play'}
            </Button>
          </CompactButtonStyles>
        )}
      </PlayerRow>
    </div>
  );
}
